import React, { useMemo } from 'react';
import { IconAlertTriangle, IconArmchair, IconCalendar, IconClock, IconLock, IconUser } from '@tabler/icons-react';

import { formatCents } from '@src/common';
import { LayoutColors } from '@src/core/theme';
import useTranslate from '@src/i18n/useTranslate';
import { groupTariffsByType } from '@src/domains/tariffs';
import Alert from '@src/components/reusable/Alert';
import ProductImage from '@src/widgets/DirectLink/Checkout/Summary/ProductImage';
import { DetailedSaleItem, RescheduleChargeSummary, ScheduleTime } from '@src/core/buyerApiClient';

import {
  Container,
  AlertHeaderContainer,
  AlertTitle,
  AlertContent,
  SummaryContent,
  SummaryProductInfo,
  SummaryProductInfoContent,
  ProductName,
  SummaryScheduleInfo,
  SummaryProductInfoContentItem,
  TotalContainer,
  TotalItem,
  OriginalAmount,
  NewAmount,
  TotalToPayLabel,
  TotalToPayValue,
  DisclaimerContent,
  DisclaimerContainer,
} from './styles';
import useBreakpoint from '@src/hooks/useBreakpoint';
import { formatScheduleDate } from '@src/domains/scheduling';

interface RescheduleSummaryProps {
  date: string;
  time?: ScheduleTime;
  chargeSummary?: RescheduleChargeSummary;
  saleItem: DetailedSaleItem;
  requiresAdditionalPayment: boolean;
  remainingRescheduleCount: number;
}

const RescheduleSummary = (props: RescheduleSummaryProps) => {
  const { date, time, chargeSummary, saleItem, requiresAdditionalPayment, remainingRescheduleCount } = props;

  const { i18n, locale } = useTranslate();
  const { isMobile } = useBreakpoint();

  const { scheduleDate, tariffs = [], resources, product } = saleItem;

  const tariffsString = useMemo(() => {
    const grouped = groupTariffsByType(tariffs);

    return grouped.map((tariff) => `${tariff.quantity}x ${tariff.tariff.type.name}`).join(', ');
  }, [tariffs]);

  const resourcesString = useMemo(() => {
    if (!resources || resources.length === 0) return null;

    return resources.map(({ resource: { name } }) => name).join(', ');
  }, [resources]);

  return (
    <Container data-testid='reschedule-summary'>
      <Alert severity='warning' variant='ghost' data-testid='reschedule-charge-summary-alert'>
        <AlertHeaderContainer data-testid='reschedule-charge-summary-alert-header'>
          <IconAlertTriangle />

          <AlertTitle>{i18n('reschedule_charge_summary_alert_title')}</AlertTitle>
        </AlertHeaderContainer>

        <AlertContent data-testid='reschedule-charge-summary-alert-content'>
          {i18n('reschedule_charge_summary_alert_description', {
            date: formatScheduleDate(scheduleDate!, locale),
            remainingRescheduleCount: remainingRescheduleCount - 1,
          })}
        </AlertContent>
      </Alert>

      <SummaryContent>
        <SummaryProductInfo>
          {product.thumbnailUrl ? (
            <ProductImage
              product={product}
              width={isMobile ? '100%' : '130px'}
              height={isMobile ? '155px' : '130px'}
              objectFit={isMobile ? 'cover' : 'contain'}
            />
          ) : null}

          <SummaryProductInfoContent>
            <ProductName>{product.name}</ProductName>

            {date ? (
              <SummaryScheduleInfo>
                <SummaryProductInfoContentItem>
                  <IconCalendar size={20} />
                  <span>{formatScheduleDate(date, locale)}</span>
                </SummaryProductInfoContentItem>

                {time ? (
                  <SummaryProductInfoContentItem>
                    <IconClock size={20} />
                    <span>{time.formattedTime}</span>
                  </SummaryProductInfoContentItem>
                ) : null}
              </SummaryScheduleInfo>
            ) : null}

            {tariffsString ? (
              <SummaryProductInfoContentItem>
                <IconUser size={20} />
                <span>{tariffsString}</span>
              </SummaryProductInfoContentItem>
            ) : null}

            {resourcesString ? (
              <SummaryProductInfoContentItem>
                <IconArmchair size={20} />
                <span>{resourcesString}</span>
              </SummaryProductInfoContentItem>
            ) : null}
          </SummaryProductInfoContent>
        </SummaryProductInfo>

        {requiresAdditionalPayment && chargeSummary ? (
          <TotalContainer>
            <TotalItem>
              <OriginalAmount>{i18n('reschedule_charge_summary_actual_schedule')}</OriginalAmount>
              <OriginalAmount>{formatCents(chargeSummary.originalAmount.amountCents)}</OriginalAmount>
            </TotalItem>

            <TotalItem>
              <NewAmount>{i18n('reschedule_charge_summary_new_schedule')}</NewAmount>
              <NewAmount>{formatCents(chargeSummary.newAmount.amountCents)}</NewAmount>
            </TotalItem>

            <TotalItem>
              <TotalToPayLabel>{i18n('reschedule_charge_summary_total_to_pay')}</TotalToPayLabel>
              <TotalToPayValue>{formatCents(chargeSummary.adjustmentAmount.amountCents)}</TotalToPayValue>
            </TotalItem>
          </TotalContainer>
        ) : null}

        <DisclaimerContainer>
          <IconLock size={20} color={LayoutColors.blackMain} />
          <DisclaimerContent>
            {i18n('reschedule_charge_summary_disclaimer', { date: formatScheduleDate(scheduleDate!, locale) })}
          </DisclaimerContent>
        </DisclaimerContainer>
      </SummaryContent>
    </Container>
  );
};

export default RescheduleSummary;
